import React from 'react';
import { TrendingUp as TrendUp, TrendingDown as TrendDown, Minus } from 'lucide-react';

const COLOR_CLASSES = {
  blue: 'bg-[#E8F2FC] border-[#156ACA]/30 text-[#156ACA]',
  green: 'bg-[#EEF9E8] border-[#56BB31]/30 text-[#56BB31]',
  orange: 'bg-orange-50 border-orange-200 text-orange-600',
  purple: 'bg-purple-50 border-purple-200 text-purple-600'
};

/**
 * Period-over-period delta badge.
 * `delta` is { pct, current?, previous?, vs_label? } or null/undefined.
 * `invertColor` flips green/red — true when LOWER is better (e.g. cost_per_mt).
 */
export const DeltaBadge = ({ delta, invertColor = false, testidPrefix = '' }) => {
  if (!delta || delta.pct === null || delta.pct === undefined) return null;
  const { pct, vs_label: vsLabel, current, previous } = delta;
  const isFlat = pct === 0;
  const isUp = pct > 0;
  const goodWhenUp = !invertColor;

  // Determine whether the change is good/bad (or flat)
  let isGood = null;
  if (!isFlat) {
    isGood = isUp ? goodWhenUp : !goodWhenUp;
  }

  // Choose the matching icon
  let Icon = Minus;
  if (!isFlat) {
    Icon = isUp ? TrendUp : TrendDown;
  }

  let tone;
  if (isGood === null) tone = 'text-gray-500 bg-gray-100';
  else if (isGood) tone = 'text-emerald-700 bg-emerald-100';
  else tone = 'text-red-700 bg-red-100';

  // Build a richer tooltip when absolute values are available
  const fmt = (n) => (typeof n === 'number' ? n.toLocaleString('en-IN', { maximumFractionDigits: 2 }) : '');
  const tooltipParts = [];
  if (typeof current === 'number') tooltipParts.push(`Now: ${fmt(current)}`);
  if (typeof previous === 'number') tooltipParts.push(`Prev: ${fmt(previous)}`);
  if (vsLabel) tooltipParts.push(`(${vsLabel})`);
  const tooltip = tooltipParts.length > 0 ? tooltipParts.join(' · ') : (vsLabel || '');

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${tone} cursor-help`}
      title={tooltip}
      data-testid={`${testidPrefix}delta-badge`}
    >
      <Icon className="h-3 w-3" />
      {isFlat ? 'flat' : `${isUp ? '+' : ''}${pct.toFixed(1)}%`}
    </span>
  );
};

export const SummaryCard = ({ title, value, color, icon, onClick, summaryKey, delta, deltaInvert }) => (
  <div
    className={`p-4 rounded-lg border-2 ${COLOR_CLASSES[color]} ${onClick ? 'cursor-pointer hover:shadow-lg hover:scale-105 transition-all' : ''}`}
    data-testid={`summary-card-${title.toLowerCase().replace(/\s/g, '-')}`}
    onClick={onClick ? () => onClick(summaryKey, title) : undefined}
  >
    <div className="flex items-center justify-between">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <DeltaBadge delta={delta} invertColor={deltaInvert} testidPrefix={`${summaryKey || title.toLowerCase()}-`} />
        </div>
        <p className="text-2xl font-bold">{value}</p>
        {delta?.vs_label && <p className="text-xs text-gray-500 mt-1 truncate">{delta.vs_label}</p>}
        {onClick && !delta?.vs_label && <p className="text-xs opacity-70 mt-1">Click for monthly breakdown</p>}
      </div>
      <div className="h-10 w-10">{icon}</div>
    </div>
  </div>
);

export function MetricCard({ label, value, onClick, metricKey, subValue, subLabel }) {
  const testIdBase = `metric-card-${(metricKey || label).toString().toLowerCase().replace(/\s/g, '-')}`;
  const wrapClass = `bg-gray-50 p-3 rounded-lg ${onClick ? 'cursor-pointer hover:bg-[#E8F2FC] hover:border-[#156ACA]/30 border-2 border-transparent transition-all' : ''}`;
  const handleClick = onClick ? () => onClick(metricKey, label) : undefined;
  // Side-by-side primary + secondary value rendering when subValue is provided.
  const valueRow = subValue ? (
    <div className="flex items-baseline gap-2 flex-wrap">
      <p className="text-lg font-semibold text-gray-800">{value}</p>
      <span className="h-4 w-px bg-gray-300" aria-hidden="true"></span>
      <p
        className="text-sm font-semibold text-[#156ACA]"
        data-testid={`${testIdBase}-sub`}
        title={subLabel ? `${subLabel}: ${subValue}` : subValue}
      >
        {subValue}{subLabel ? <span className="text-xs text-gray-500 font-normal ml-1">{subLabel}</span> : null}
      </p>
    </div>
  ) : (
    <p className="text-lg font-semibold text-gray-800">{value}</p>
  );
  return (
    <div className={wrapClass} data-testid={testIdBase} onClick={handleClick}>
      <p className="text-xs text-gray-500">{label}</p>
      {valueRow}
      {onClick ? <p className="text-xs text-[#156ACA] mt-1">Click for details</p> : null}
    </div>
  );
}
